import { useRef, useState } from 'react'
import {
    AlertTriangle,
    Check,
    ShieldCheck,
    TrendingUp
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { smoothPath, toPoints } from '@/lib/chart'
import { useInView } from '@/hooks/use-in-view'
import { CountUp } from '@/components/generals/count-up'

const panel = 'glass-dark grain relative overflow-hidden rounded-[30px] p-5 sm:p-7'

const ENVELOPES = [
    { label: 'Groceries', spent: 412, budget: 600 },
    { label: 'Eating out', spent: 164, budget: 250 },
    { label: 'Getting around', spent: 96, budget: 180 },
    { label: 'Just for fun', spent: 182, budget: 200 }
]

export const BudgetPreview = () => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.4 })

    return (
        <div ref={ref} className={panel}>
            <div className='flex items-start justify-between gap-4'>
                <div>
                    <p className='text-xs text-cream/55'>
                        Yours to spend today
                    </p>

                    <p className='mt-1 text-5xl font-normal tracking-[-0.045em] text-cream'>
                        <CountUp value={86} format='currency' active={inView} />
                    </p>
                </div>

                <span className='rounded-full bg-citron/15 px-3 py-1 text-xs text-citron'>
                    On pace
                </span>
            </div>

            <ul className='mt-7 space-y-4'>
                {ENVELOPES.map((item, index) => {
                    const share = Math.round((item.spent / item.budget) * 100)
                    const tight = share > 85

                    return (
                        <li key={item.label}>
                            <div className='flex items-baseline justify-between text-[13px]'>
                                <span className='text-cream/80'>
                                    {item.label}
                                </span>

                                <span className='tabular-nums text-cream/55'>
                                    {formatCurrency(item.spent)} of {formatCurrency(item.budget)}
                                </span>
                            </div>

                            <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.08]'>
                                <div
                                    className={cn('h-full rounded-full transition-[width] duration-[1600ms] ease-cinema', tight ? 'bg-[linear-gradient(90deg,#e8b65d,#d9874a)]' : 'bg-[linear-gradient(90deg,#e2f37a,#8fae62)]')}
                                    style={{ width: inView ? `${share}%` : '0%', transitionDelay: `${index * 140}ms` }}
                                />
                            </div>
                        </li>
                    )
                })}
            </ul>

            <p className='mt-6 rounded-2xl bg-white/[0.04] px-4 py-3 text-xs leading-relaxed text-cream/70'>
                Fun spending is close to its limit. Keeping $18 in reserve until Friday holds the month steady.
            </p>
        </div>
    )
}

const ALLOCATION = [
    { label: 'US equities', share: 46, color: '#e2f37a' },
    { label: 'Global equities', share: 22, color: '#8fae62' },
    { label: 'Bonds', share: 20, color: '#7fb8b0' },
    { label: 'Impact funds', share: 8, color: '#a3a9f2' },
    { label: 'Cash', share: 4, color: 'rgba(243,239,230,0.4)' }
]

const PERFORMANCE = [100, 101.4, 100.8, 102.9, 104.1, 103.2, 105.8, 107.3, 106.6, 108.9, 110.4, 112.1]

export const InvestPreview = () => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.4 })
    const points = toPoints(PERFORMANCE, { width: 260, height: 70, padTop: 6, padBottom: 6 })

    return (
        <div ref={ref} className={panel}>
            <div className='flex items-start justify-between gap-4'>
                <div>
                    <p className='text-xs text-cream/55'>
                        Your portfolio
                    </p>

                    <p className='mt-1 text-4xl font-normal tracking-[-0.045em] text-cream'>
                        <CountUp value={96310.55} decimals={2} format='currency' active={inView} />
                    </p>
                </div>

                <span className='inline-flex items-center gap-1 rounded-full bg-citron/15 px-3 py-1 text-xs text-citron'>
                    <TrendingUp aria-hidden='true' className='size-3.5' />
                    Up 12.1% this year
                </span>
            </div>

            <svg viewBox='0 0 260 70' className='mt-5 h-[70px] w-full overflow-visible' aria-hidden='true'>
                <path
                    d={smoothPath(points)}
                    fill='none'
                    stroke='#e2f37a'
                    strokeWidth='2'
                    strokeLinecap='round'
                    pathLength={1}
                    strokeDasharray='1'
                    style={{ strokeDashoffset: inView ? 0 : 1, transition: 'stroke-dashoffset 1.8s cubic-bezier(0.22, 1, 0.36, 1)' }}
                />
            </svg>

            <div className='mt-6 flex h-2.5 overflow-hidden rounded-full bg-white/[0.06]'>
                {ALLOCATION.map((item, index) => (
                    <span
                        key={item.label}
                        className='h-full transition-[width] duration-[1400ms] ease-cinema'
                        style={{ width: inView ? `${item.share}%` : '0%', backgroundColor: item.color, transitionDelay: `${300 + index * 90}ms` }}
                    />
                ))}
            </div>

            <ul className='mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-xs'>
                {ALLOCATION.map(item => (
                    <li key={item.label} className='flex items-center justify-between gap-2'>
                        <span className='flex items-center gap-1.5 text-cream/70'>
                            <span className='size-1.5 rounded-full' style={{ backgroundColor: item.color }} />
                            {item.label}
                        </span>

                        <span className='tabular-nums text-cream/50'>
                            {item.share}%
                        </span>
                    </li>
                ))}
            </ul>

            <p className='mt-6 text-xs text-cream/45'>
                Last rebalanced on its own 3 days ago. Yearly fees: 0.08%.
            </p>
        </div>
    )
}

const MILESTONES = [
    { label: 'A season in Portugal', when: 'Spring 2027', share: 82 },
    { label: 'Down payment on a home', when: '2028', share: 63 },
    { label: "Ada's college fund", when: '2041', share: 18 },
    { label: 'Retiring at 60', when: '2052', share: 34 }
]

export const PlanPreview = () => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.4 })
    const circumference = 2 * Math.PI * 16

    return (
        <div ref={ref} className={panel}>
            <p className='text-xs text-cream/55'>
                Your goals, in sequence
            </p>

            <ol className='relative mt-5 space-y-5 before:absolute before:bottom-3 before:left-[19px] before:top-3 before:w-px before:bg-white/10'>
                {MILESTONES.map((item, index) => (
                    <li key={item.label} className='relative flex items-center gap-4'>
                        <span className='relative grid size-10 shrink-0 place-items-center rounded-full bg-ink'>
                            <svg viewBox='0 0 40 40' className='absolute inset-0 size-10 -rotate-90' aria-hidden='true'>
                                <circle cx='20' cy='20' r='16' fill='none' stroke='rgba(255,255,255,0.08)' strokeWidth='3' />
                                <circle
                                    cx='20'
                                    cy='20'
                                    r='16'
                                    fill='none'
                                    stroke='#e2f37a'
                                    strokeWidth='3'
                                    strokeLinecap='round'
                                    strokeDasharray={circumference}
                                    strokeDashoffset={inView ? circumference * (1 - item.share / 100) : circumference}
                                    style={{ transition: `stroke-dashoffset 1.6s cubic-bezier(0.22, 1, 0.36, 1) ${index * 150}ms` }}
                                />
                            </svg>

                            <span className='relative text-[10px] tabular-nums text-cream/80'>
                                {item.share}%
                            </span>
                        </span>

                        <span className='min-w-0 flex-1'>
                            <span className='block truncate text-[14px] text-cream'>
                                {item.label}
                            </span>

                            <span className='block text-xs text-cream/45'>
                                {item.when}
                            </span>
                        </span>
                    </li>
                ))}
            </ol>

            <p className='mt-6 rounded-2xl bg-citron/10 px-4 py-3 text-xs leading-relaxed text-citron'>
                Pushing Portugal back by three months frees $240 a month for the down payment.
            </p>
        </div>
    )
}

const CHECKS = [
    { label: 'Rainy-day fund', detail: '4.2 months of bills covered', done: true },
    { label: 'Fraud watch', detail: 'Active on every card', done: true },
    { label: 'Life insurance', detail: 'Checked in March', done: true },
    { label: 'Income cover', detail: 'Worth a second look', done: false }
]

export const ProtectPreview = () => {
    const [decision, setDecision] = useState<'pending' | 'mine' | 'blocked'>('pending')

    return (
        <div className={panel}>
            <div className='flex items-center justify-between gap-4'>
                <div>
                    <p className='text-xs text-cream/55'>
                        Resilience score
                    </p>

                    <p className='mt-1 text-5xl font-normal tracking-[-0.045em] text-cream'>
                        86
                        <span className='text-xl text-cream/40'>
                            /100
                        </span>
                    </p>
                </div>

                <ShieldCheck aria-hidden='true' className='size-10 text-citron' strokeWidth={1.4} />
            </div>

            <ul className='mt-6 divide-y divide-white/[0.06]'>
                {CHECKS.map(item => (
                    <li key={item.label} className='flex items-center gap-3 py-3'>
                        <span className={cn('grid size-6 shrink-0 place-items-center rounded-full', item.done ? 'bg-citron/15 text-citron' : 'bg-[#e8b65d]/15 text-[#e8b65d]')}>
                            {item.done ? <Check aria-hidden='true' className='size-3.5' /> : <AlertTriangle aria-hidden='true' className='size-3.5' />}
                        </span>

                        <span className='flex-1 text-[13px] text-cream/85'>
                            {item.label}
                        </span>

                        <span className='text-xs text-cream/45'>
                            {item.detail}
                        </span>
                    </li>
                ))}
            </ul>

            <div className='mt-5 rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10' aria-live='polite'>
                {decision === 'pending' && (
                    <>
                        <p className='text-[13px] leading-relaxed text-cream/85'>
                            A {formatCurrency(412)} charge from an online shop in Lisbon is on hold. Did you make it?
                        </p>

                        <div className='mt-3 flex gap-2'>
                            <button type='button' onClick={() => setDecision('mine')} className='rounded-full bg-cream px-4 py-2 text-xs font-medium text-black transition-colors hover:bg-white'>
                                Yes, mine
                            </button>

                            <button type='button' onClick={() => setDecision('blocked')} className='rounded-full px-4 py-2 text-xs text-cream/70 ring-1 ring-white/15 transition-colors hover:text-cream'>
                                Not mine
                            </button>
                        </div>
                    </>
                )}

                {decision === 'mine' && (
                    <p className='text-[13px] leading-relaxed text-cream/85'>
                        Thank you. The payment is through, and this shop is now on your trusted list.
                    </p>
                )}

                {decision === 'blocked' && (
                    <p className='text-[13px] leading-relaxed text-cream/85'>
                        Stopped. Your card is on hold, a new one is in the post, and not a cent left your account.
                    </p>
                )}
            </div>
        </div>
    )
}
